import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { answerPlus, getPlusQuestion } from "../api";
import MutationError from "../components/MutationError";
import { Pending, TextLines } from "../components/Placeholder";
import { useT } from "../i18n";
import { useSession } from "../session";

/**
 * PlusPage is what the Get Plus tap opens (requirement 0125): the good news,
 * then one question, and Continue — greyed out until an answer is picked.
 * The answer grants Plus.
 *
 * Its fetch of the question is the tap the server counts (ADR-0190 §5), so it
 * is made once, on this page, and never prefetched. The choices are the
 * server's keys; their wording, and the price in the question, are ours.
 */
export default function PlusPage() {
  const t = useT();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isSignedIn, isPending: sessionPending } = useSession();
  const [params] = useSearchParams();
  const bookID = params.get("book") ?? undefined;
  // Only a path inside the app: `then` is never a way off the site.
  const thenParam = params.get("then");
  const then = thenParam?.startsWith("/") && !thenParam.startsWith("//") ? thenParam : null;
  const after = then ?? (bookID ? `/books/${bookID}` : "/browse");

  const [answer, setAnswer] = useState<string>("");
  const [note, setNote] = useState("");

  const question = useQuery({
    queryKey: ["plus-question"],
    queryFn: () => getPlusQuestion(bookID),
    enabled: isSignedIn,
    // One tap, one fetch: a refocus is not another tap.
    staleTime: Infinity,
    gcTime: 0,
    retry: false,
  });

  const grant = useMutation({
    mutationFn: () =>
      answerPlus({ question: question.data!.question, answer, note: note.trim() || undefined }),
    onSuccess: async () => {
      // Everything a locked book said about itself is stale now.
      await queryClient.invalidateQueries();
      navigate(after, { replace: true });
    },
  });

  if (!sessionPending && !isSignedIn) return <Navigate to="/browse" replace />;
  if (question.data?.plus) return <Navigate to={after} replace />;

  return (
    <main className="mx-auto max-w-md px-4 pt-8 pb-12" data-testid="plus-page">
      {question.isPending ? (
        <Pending label={t.common.loading}>
          <TextLines lines={3} />
        </Pending>
      ) : question.isError ? (
        <p className="text-sm text-muted">{t.common.somethingWrongTryAgain}</p>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (answer) grant.mutate();
          }}
        >
          <p data-testid="plus-good-news" className="text-lg leading-7 font-semibold">
            {t.plus.goodNews}
          </p>
          <h1 data-testid="plus-question" className="mt-6 text-base font-medium">
            {t.plus.question}
          </h1>
          {/* Three large choices, stacked: a tap target each on a phone,
              and the picked one unmistakable. Not ChipRow's small pills —
              this is the screen's one decision, not a setting. */}
          <fieldset className="mt-3 grid gap-2">
            <legend className="sr-only">{t.plus.question}</legend>
            {question.data.choices.map((c) => (
              <button
                key={c}
                type="button"
                aria-pressed={answer === c}
                disabled={grant.isPending}
                data-testid={`plus-choice-${c}`}
                onClick={() => setAnswer(c)}
                className={`rounded-control border px-4 py-3 text-start text-[0.95rem] ${
                  answer === c
                    ? "border-sage bg-sage font-semibold text-on-sage"
                    : "border-line bg-surface text-ink hover:border-sage/60"
                }`}
              >
                {t.plus.choices[c] ?? c}
              </button>
            ))}
          </fieldset>
          <label className="mt-5 block text-sm text-ink-soft">
            {t.plus.noteLabel}
            <input
              type="text"
              dir="auto"
              maxLength={500}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid="plus-note"
              className="mt-1 block w-full rounded-control border border-line bg-surface px-3 py-2 text-[0.95rem] text-ink"
            />
          </label>
          <button
            type="submit"
            disabled={!answer || grant.isPending}
            data-testid="plus-continue"
            className="mt-6 w-full rounded-full bg-sage px-5 py-2.5 text-[0.95rem] font-semibold text-on-sage disabled:bg-sunk disabled:text-muted"
          >
            {t.plus.continue}
          </button>
          {!answer && (
            <p className="mt-2 text-center text-xs text-muted">{t.plus.pickToContinue}</p>
          )}
          <MutationError
            error={grant.error}
            fallback={t.common.somethingWrongTryAgain}
            className="mt-3"
          />
          <p data-testid="plus-terms" className="mt-8 text-xs leading-5 text-muted">
            {t.plus.terms}
          </p>
        </form>
      )}
    </main>
  );
}
